import React, { useState, useEffect, useCallback } from 'react';
import { Query, useQuery } from 'react-apollo';
import debounce from 'lodash/debounce';
import {
  GET_STUDENT_PROFILE,
  GET_GUARDIANS,
  ADD_GUARDIAN,
  UPDATE_GUARDIAN,
  UPDATE_STUDENT_PROFILE,
} from 'graphql/student/Profile';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Form from 'components/form';
import Input from 'components/form/Input';
import PhoneInput from 'components/form/PhoneInput';
import Select from 'components/form/Select';
import Spin from 'components/common/Spin';
import Divider from '@material-ui/core/Divider';
import Button from 'components/common/Button';
import useStyles from '../styles';
import InvolvementInput from './InvolvementInput';
import useSnack from 'hooks/useSnack';
import { useMutation } from '@apollo/react-hooks';
import extend from 'lodash/extend';
import { validatePhone } from 'utils';
import omit from 'lodash/omit';
import { API } from 'aws-amplify';

const FormItem = Form.FormItem;

const FREQUENCY = [
  { label: 'Every day', value: 'daily' },
  { label: 'Few times in a week', value: 'weekly' },
  { label: 'Few times in a month', value: 'monthly' },
];

const EDUCATION_LEVEL = [
  { label: 'Some high school, no diploma', value: 'nodiploma' },
  { label: 'High school diploma', value: 'highschool' },
  { label: 'Some college credit, no degree', value: 'nodegree' },
  { label: 'Trade/technical/vocational training', value: 'training' },
  { label: 'Associate degree', value: 'associate' },
  { label: 'Bachelor’s degree', value: 'bachelor' },
  { label: 'Master’s degree', value: 'master' },
  { label: 'Doctorate/Professional degree', value: 'professional' },
];

const GuardianForm = Form.create()(
  ({ form, serial, user, guardian, afterUpdate, editable }) => {
    const { validateFields, getFieldDecorator } = form;
    const [updateGuardian] = useMutation(UPDATE_GUARDIAN);
    const [addGuardian] = useMutation(ADD_GUARDIAN);
    const snack = useSnack();

    const onSubmit = () => {
      return validateFields().then((values) => {
        let relationship = values.relationship;
        let education_level = values.education_level.value;
        const colleges = values.colleges
          ? values.colleges.map((i) => i.value)
          : [];

        values = omit(values, ['relationship', 'education_level', 'colleges']);

        if (guardian.id) {
          return updateGuardian({
            variables: {
              userId: guardian.guardian_id,
              data: values,
              relationshipInfo: {
                relationship: relationship,
                colleges,
                education_level,
              },
            },
          }).then(
            () => snack.success('Success!'),
            (e) => {
              const { message } = e;
              if (message.includes('users_user_phone_key')) {
                snack.error(
                  'Phone number already in use. Try a different number.',
                );
              } else {
                snack.error(message);
              }
            },
          );
        } else {
          return addGuardian({
            variables: {
              firstName: values.user_first_name,
              lastName: values.user_last_name,
              email: values.user_email,
              phone: values.user_phone,
              studentId: user.id,
              relationship,
            },
          }).then(
            () => {
              afterUpdate();
              snack.success('Success!');
            },
            (e) => {
              const { message } = e;
              if (message.includes('users_user_phone_key')) {
                snack.error(
                  'Phone number already in use. Try a different number.',
                );
              } else {
                snack.error(message);
              }
            },
          );
        }
      });
    };

    const classes = useStyles();

    const fetchColleges = useCallback(
      debounce((query) => {
        return API.get('api', '/college/search', {
          queryStringParameters: {
            name: query,
            fields: 'id,school.name',
          },
        }).then((resp) => {
          return resp.results.map((i) => ({
            label: i['school.name'],
            value: i['school.name'],
          }));
        });
      }, 300),
      [],
    );

    return (
      <Card className={classes.card}>
        <CardContent className={classes.cardContent}>
          <Typography gutterBottom variant="h5">
            Parent/Guardian {serial}
          </Typography>

          <Typography paragraph variant="caption" align="center">
            Enter the contact information for your parent/guardian so they can
            stay up-to-date with your progress in the WeAdmit program.
          </Typography>
          <Box marginTop={3} width="100%" maxWidth={500}>
            <Form>
              <FormItem>
                {getFieldDecorator(`user_first_name`, {
                  initialValue: guardian.guardian?.user_first_name || '',
                })(
                  <Input
                    disabled={!editable}
                    label="First name"
                    placeholder="Enter Parent/Guardian first name"
                  />,
                )}
              </FormItem>
              <FormItem>
                {getFieldDecorator(`user_last_name`, {
                  initialValue: guardian.guardian?.user_last_name || '',
                })(
                  <Input
                    disabled={!editable}
                    label="Last name"
                    placeholder="Enter Parent/Guardian last name"
                  />,
                )}
              </FormItem>
              <FormItem>
                {getFieldDecorator(`user_email`, {
                  rules: [
                    { required: true, message: 'Email id required' },
                    { type: 'email', message: 'Please enter a valid email' },
                  ],
                  initialValue: guardian.guardian?.user_email || '',
                })(
                  <Input
                    disabled={!editable}
                    label="Email"
                    placeholder="Enter Parent/Guardian email"
                  />,
                )}
              </FormItem>
              <FormItem>
                {getFieldDecorator(`user_phone`, {
                  initialValue: guardian.guardian?.user_phone || '',
                  rules: [
                    {
                      required: serial === 1,
                      message: 'Phone number id required',
                    },
                    { validator: validatePhone },
                  ],
                })(
                  <PhoneInput
                    disabled={!editable}
                    label="Phone number"
                    placeholder="Enter Parent/Guardian phone number"
                  />,
                )}
              </FormItem>
              <FormItem>
                {getFieldDecorator(`relationship`, {
                  initialValue: guardian?.relationship || '',
                })(
                  <Input
                    disabled={!editable}
                    label="Relationship to you"
                    placeholder="Mother, Father, Sister, Guardian..."
                  />,
                )}
              </FormItem>
              <FormItem>
                {getFieldDecorator('education_level', {
                  initialValue: EDUCATION_LEVEL.find(
                    (i) => i.value === guardian?.education_level || '',
                  ),
                  rules: [
                    { required: true, message: 'This field is required' },
                  ],
                })(
                  <Select
                    disabled={!editable}
                    isMulti={false}
                    label="Highest Degree or Level of Education:"
                    placeholder="Select a option"
                    options={EDUCATION_LEVEL}
                  />,
                )}
              </FormItem>
              <FormItem>
                {getFieldDecorator('colleges', {
                  initialValue: Array.isArray(guardian?.colleges)
                    ? guardian?.colleges.map((i) => ({ label: i, value: i }))
                    : [],
                })(
                  <Select
                    async
                    disabled={!editable}
                    isMulti
                    placeholder="Search for college..."
                    label="Colleges attended"
                    loadOptions={fetchColleges}
                  />,
                )}
              </FormItem>
            </Form>
          </Box>
        </CardContent>
        <Divider />
        <CardContent className={classes.footer}>
          <Button
            size="large"
            variant="contained"
            color="primary"
            onClick={onSubmit}
            disabled={!editable}
          >
            Save
          </Button>
        </CardContent>
      </Card>
    );
  },
);

const InvolvementForm = ({
  getFieldDecorator,
  onSubmit,
  parent_info,
  editable,
}) => {
  const classes = useStyles();
  return (
    <Card className={classes.card}>
      <CardContent className={classes.cardContent}>
        <Typography variant="h5">Parents/Guardian involvement</Typography>
        <Box width="100%" maxWidth={500} marginTop={3}>
          <Form>
            <FormItem>
              {getFieldDecorator('college_topic_frequency', {
                initialValue: FREQUENCY.find(
                  (i) => i.value === parent_info?.college_topic_frequency,
                ),
              })(
                <Select
                  disabled={!editable}
                  isMulti={false}
                  label="How often does the topic of college come up with your parent/guardians?"
                  placeholder="Select a frequency"
                  options={FREQUENCY}
                />,
              )}
            </FormItem>
            <FormItem>
              {getFieldDecorator('parent_involvement_you_like', {
                initialValue: parent_info?.parent_involvement_you_like,
              })(
                <InvolvementInput
                  disabled={!editable}
                  label="How involved would you like your parents/guardians to be in your college application process?"
                />,
              )}
            </FormItem>
            <FormItem>
              {getFieldDecorator('parent_involvement_parents_like', {
                initialValue: parent_info?.parent_involvement_parents_like,
              })(
                <InvolvementInput
                  disabled={!editable}
                  label="How involved do you think your parents/guardians would like to be in your college application process?"
                />,
              )}
            </FormItem>
          </Form>
        </Box>
      </CardContent>
      <Divider />
      <CardContent className={classes.footer}>
        <Button
          disabled={!editable}
          size="large"
          variant="contained"
          color="primary"
          onClick={onSubmit}
        >
          Save
        </Button>
      </CardContent>
    </Card>
  );
};

const GuardianInfo = ({
  user,
  form: { getFieldDecorator, validateFields },
  editable,
}) => {
  const [guardians, setGuardians] = useState([]);
  const snack = useSnack();
  const { data, refetch } = useQuery(GET_GUARDIANS, {
    variables: { userId: user.id },
    fetchPolicy: 'cache-and-network',
  });
  const [update] = useMutation(UPDATE_STUDENT_PROFILE);

  useEffect(() => {
    if (data) {
      if (data.guardians.length === 2) {
        setGuardians(data.guardians);
      } else {
        setGuardians([data.guardians[0] || {}, {}]);
      }
    } else {
      setGuardians([{}, {}]);
    }
  }, [data]);

  return (
    <Query query={GET_STUDENT_PROFILE} variables={{ id: user.id }}>
      {({ data }) => {
        if (!data) return <Spin spinning />;
        const profile = data.student_profile[0];

        const onSubmit = () => {
          return validateFields().then((values) => {
            values.college_topic_frequency =
              values.college_topic_frequency?.value;

            return update({
              variables: {
                id: user.id,
                input: {
                  parent_info: extend(profile.parent_info, values),
                },
              },
            }).then(
              () => {
                snack.success('Profile saved');
              },
              (e) => {
                snack.error(e.message);
              },
            );
          });
        };

        return (
          <Form>
            {guardians.map((g, idx) => (
              <GuardianForm
                key={idx}
                serial={idx + 1}
                guardian={g}
                user={user}
                afterUpdate={refetch}
                editable={editable}
              />
            ))}
            <InvolvementForm
              getFieldDecorator={getFieldDecorator}
              parent_info={profile.parent_info}
              user={user}
              onSubmit={onSubmit}
              editable={editable}
            />
          </Form>
        );
      }}
    </Query>
  );
};

export default Form.create()(GuardianInfo);
